import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'

import { ENVELOPE_ACCESS_TOKEN_HEADER } from '@/lib/envelope-access'
import { logger } from '@/lib/logger'
import { ensureFreshToken } from '@/lib/refresh-token'

import {
  addAuthHeaders,
  addCustomHeaders,
  addRequestMetadata,
  calculateRequestDuration,
  createMalformedResponseError,
  extractTelemetryId,
  handle403SandboxError,
  handleAuthFailure,
  handleSilentSignout,
  isAuthEndpoint,
  isExternalRequest,
  isMalformedResponse,
  logFailedRequest,
  logRequestStart,
  logSuccessfulResponse,
  materializeFormDataFiles,
} from './api-client.utils'
import { isLogoutOnAuthFailureForced } from './auth-failure-override'

const API_URL = import.meta.env.VITE_API_URL

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    ['Content-Type']: 'application/json',
  },
})

// Request interceptor to add authentication token, custom headers, and track metadata
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let updatedConfig = config

    // Apply all request transformations
    updatedConfig = addRequestMetadata(updatedConfig)

    // Only add Signater-specific headers for internal API requests
    if (!isExternalRequest(updatedConfig)) {
      updatedConfig = addAuthHeaders(updatedConfig)
      updatedConfig = addCustomHeaders(updatedConfig)
    }

    updatedConfig = await materializeFormDataFiles(updatedConfig)

    // Log the request
    logRequestStart(updatedConfig)

    return updatedConfig
  },
  (error) => {
    logger.error(
      'API request configuration failed',
      error instanceof Error ? error : undefined,
      {
        message: error?.message,
      },
      'api',
    )
    return Promise.reject(error)
  },
)

// Response interceptor to log responses and handle automatic refresh token
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const duration = calculateRequestDuration(response.config)
    const telemetryOperationId = extractTelemetryId(response)

    if (isMalformedResponse(response)) {
      const error = createMalformedResponseError(response)
      logFailedRequest(error, duration, telemetryOperationId)
      return Promise.reject(error)
    }

    logSuccessfulResponse(response, duration, telemetryOperationId)

    return response
  },
  async (error: unknown) => {
    if (!isAxiosError(error)) {
      return Promise.reject(error)
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    const duration = calculateRequestDuration(originalRequest)
    const telemetryOperationId = extractTelemetryId(error.response)

    logFailedRequest(error, duration, telemetryOperationId)

    // Handle 401 errors with token refresh logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isAuthEndpoint(originalRequest.url)) {
        handleAuthFailure(originalRequest.url, telemetryOperationId)
        return Promise.reject(error)
      }

      // If no auth was sent, this is a genuine 401 — don't try to refresh
      if (!originalRequest.headers.authorization) {
        return Promise.reject(error)
      }

      // The envelope access token was refused, not the session; its caller renews it
      if (originalRequest.headers[ENVELOPE_ACCESS_TOKEN_HEADER]) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const rejectedAccessToken = String(
          originalRequest.headers.authorization,
        ).replace(/^Bearer\s+/i, '')
        const newToken = await ensureFreshToken(rejectedAccessToken)
        originalRequest.headers.authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      } catch {
        if (isLogoutOnAuthFailureForced()) {
          window.location.href = '/logout'
          return Promise.reject(error)
        }

        // Requests that opt out of redirect to /logout
        if (originalRequest._skipRedirectOnAuthFailure) {
          handleSilentSignout(originalRequest.url, telemetryOperationId)

          // 'retry-without-auth': retry without token (for endpoints that accept optional auth)
          if (
            originalRequest._skipRedirectOnAuthFailure === 'retry-without-auth'
          ) {
            delete originalRequest.headers.authorization
            return apiClient(originalRequest)
          }

          // true: just reject (for auth-required endpoints on public pages)
          return Promise.reject(error)
        }

        // Default: redirect to logout
        window.location.href = '/logout'
        return Promise.reject(error)
      }
    }

    // Handle 403 errors when in sandbox mode (sandbox feature was disabled)
    if (error.response?.status === 403) {
      const sandboxResult = await handle403SandboxError(
        apiClient,
        error,
        telemetryOperationId,
      )
      if (sandboxResult.handled && sandboxResult.response) {
        return sandboxResult.response
      }
    }

    return Promise.reject(error)
  },
)
